const lines = (...parts) => parts.join('\n');

const indent = (text) =>
  text
    .split('\n')
    .map((line) => (line ? `    ${line}` : ''))
    .join('\n');

const program = ({
  includes = ['#include <stdio.h>'],
  helpers = '',
  mainBody = '',
  mainSignature = 'int main()',
  addReturn = true
}) => {
  const sections = [includes.join('\n')];
  const trimmedHelpers = helpers.trim();
  const trimmedBody = mainBody.trim();
  const finalBody = addReturn
    ? [trimmedBody, 'return 0;'].filter(Boolean).join('\n')
    : trimmedBody;

  if (trimmedHelpers) {
    sections.push(trimmedHelpers);
  }

  sections.push(`${mainSignature} {\n${indent(finalBody)}\n}`);
  return sections.join('\n\n');
};

const normalizeSentence = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return '';
  return /[。．!?]$/.test(trimmed) ? trimmed : `${trimmed}。`;
};

const plainText = (text) =>
  text
    .replace(/`/g, '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[。．]$/, '');

const summarizePoints = (points) => points.map(plainText).filter(Boolean).slice(0, 3).join('、');

const buildLessonDescription = ({ description, points, category, difficulty, type }) => {
  const intro = normalizeSentence(description);
  const pointSummary = summarizePoints(points);
  const levelGuide =
    difficulty === '初級'
      ? '初めて学ぶ人でも流れを追いやすいように、前提から順に整理しながら'
      : difficulty === '中級'
        ? '基礎を踏まえて理解を一段深められるように、典型的な使いどころも意識しながら'
        : '基礎知識を前提に、実践や応用場面でどう使うかまで見通せるように';
  const learningStyle =
    type === 'graphics'
      ? '描画結果の変化を見比べながら'
      : 'サンプルコードの実行結果を確かめながら';

  return `${intro}${levelGuide}${learningStyle}${pointSummary}といった点を押さえ、${category}の考え方を「書ける」だけでなく「なぜそう書くのか」まで理解できるように進めます。`;
};

const getParameterNames = (params) => {
  const trimmed = params.trim();
  if (!trimmed || trimmed === 'void') {
    return [];
  }

  return trimmed
    .split(',')
    .map((part) => part.trim())
    .map((part) => part.replace(/\[[^\]]*\]/g, '').replace(/\*/g, ' '))
    .map((part) => part.split(/\s+/).filter(Boolean).pop())
    .filter(Boolean);
};

const explainIncludeLine = (line) => {
  const includeMap = {
    '#include <stdio.h>': '標準入出力ライブラリを読み込み、`printf` や `scanf` などの基本的な入出力関数を使えるようにしています。',
    '#include <stdlib.h>': 'メモリ確保、乱数、変換処理などを含む標準ライブラリを読み込み、汎用的な関数を使えるようにしています。',
    '#include <string.h>': '文字列の長さ取得や連結など、文字列操作のための標準関数を使えるようにしています。',
    '#include <ctype.h>': '文字の種類判定や大文字・小文字変換を行うためのライブラリを読み込んでいます。',
    '#include <math.h>': '三角関数や数学関数を使うためのライブラリを読み込み、座標計算や数値解析に備えています。',
    '#include <time.h>': '現在時刻を利用した乱数初期化など、時刻関連の関数を使えるようにしています。',
    '#include "graphics.h"': 'このWebアプリに組み込まれている独自のグラフィックスライブラリを読み込み、図形描画や入力処理を使えるようにしています。'
  };

  return includeMap[line] || 'この行で必要なライブラリを読み込み、後続のコードで使う関数や型を利用可能にしています。';
};

const parseDeclarationLine = (line) => {
  if (
    /^(if|for|while|switch|return|case|default|typedef|#)/.test(line) ||
    line.includes('(') ||
    !line.endsWith(';')
  ) {
    return null;
  }

  const statement = line.slice(0, -1);
  const equalIndex = statement.indexOf('=');
  const left = (equalIndex === -1 ? statement : statement.slice(0, equalIndex)).trim();
  const initializer = equalIndex === -1 ? '' : statement.slice(equalIndex + 1).trim();
  const variableMatch = left.match(/([A-Za-z_]\w*(?:\[[^\]]*\])*)$/);

  if (!variableMatch) {
    return null;
  }

  const variableName = variableMatch[1];
  const typeText = left.slice(0, left.length - variableName.length).trim();

  if (!typeText) {
    return null;
  }

  return {
    typeText,
    variableName,
    baseName: variableName.match(/^([A-Za-z_]\w*)/)?.[1] || variableName,
    initializer
  };
};

const explainDeclarationLine = (line) => {
  const parsed = parseDeclarationLine(line);
  if (!parsed) {
    return null;
  }

  const { typeText, variableName, baseName, initializer } = parsed;
  const isArray = variableName.includes('[');
  const isPointer = typeText.includes('*');
  const isConst = /\bconst\b/.test(typeText);

  let subject = '変数';
  if (/^struct\b/.test(typeText)) subject = '構造体変数';
  if (/^enum\b/.test(typeText)) subject = '列挙型の変数';
  if (/^union\b/.test(typeText)) subject = '共用体の変数';
  if (isArray) subject = '配列';
  if (isPointer) subject = 'ポインタ変数';
  if (isConst) subject = `定数として扱う${subject}`;

  if (initializer) {
    return `${subject} \`${baseName}\` を宣言し、最初に使う値として \`${initializer}\` を代入しています。`;
  }

  return `${subject} \`${baseName}\` を宣言し、後の処理で値を入れたり更新したりできる状態を用意しています。`;
};

const explainAssignmentLine = (line) => {
  const pointerMatch = line.match(/^\*([A-Za-z_]\w*)\s*=\s*(.+);$/);
  if (pointerMatch) {
    return `ポインタ \`${pointerMatch[1]}\` が指している先の値を \`${pointerMatch[2]}\` に更新し、呼び出し元や元データへ変更を反映しています。`;
  }

  const incrementMatch = line.match(/^([A-Za-z_]\w*)\s*(\+\+|--);$/);
  if (incrementMatch) {
    return `変数 \`${incrementMatch[1]}\` を ${incrementMatch[2] === '++' ? '1増やし' : '1減らし'}、次のループや判定に向けて状態を進めています。`;
  }

  const assignmentMatch = line.match(/^([A-Za-z_]\w*(?:\[[^\]]+\])?)\s*(=|\+=|-=|\*=|\/=|%=|&=|\|=|\^=)\s*(.+);$/);
  if (!assignmentMatch) {
    return null;
  }

  const [, left, operator, right] = assignmentMatch;
  const operatorMap = {
    '=': '新しい値として代入し',
    '+=': '現在の値へ足し込み',
    '-=': '現在の値から引き算し',
    '*=': '現在の値へ掛け算し',
    '/=': '現在の値を割り算で更新し',
    '%=': '現在の値を余りで更新し',
    '&=': 'ビットANDの結果で更新し',
    '|=': 'ビットORの結果で更新し',
    '^=': 'ビットXORの結果で更新し'
  };

  return `\`${left}\` に対して \`${right}\` を使う計算を行い、${operatorMap[operator]}後続の処理で使う値を整えています。`;
};

const explainFunctionDefinitionLine = (line) => {
  const match = line.match(/^([A-Za-z_][\w\s\*]*?)\s+([A-Za-z_]\w*)\(([^)]*)\)\s*\{$/);
  if (!match) {
    return null;
  }

  const [, returnType, name, params] = match;
  if (['if', 'for', 'while', 'switch'].includes(name)) {
    return null;
  }

  if (name === 'main') {
    return 'プログラムの開始地点である `main` 関数を定義し、ここから処理が順番に実行されることを示しています。';
  }

  const paramNames = getParameterNames(params);
  const paramText =
    paramNames.length === 0
      ? '引数を受け取らずに'
      : `引数 ${paramNames.map((param) => `\`${param}\``).join('、')} を受け取り`;

  return `${paramText}\`${returnType.trim()}\` 型の結果を返す関数 \`${name}\` を定義し、このプログラムで再利用する処理をまとめています。`;
};

const explainCallLine = (line) => {
  const callMap = [
    [/^printf\(/, '画面へ文字列や計算結果を表示し、プログラムが何をしているかを確認できるようにしています。'],
    [/^scanf\(/, '標準入力から値を読み取り、以降の計算や判定で使うデータを変数へ取り込んでいます。'],
    [/^clear_canvas\(/, 'キャンバスを消去して前フレームの描画をリセットし、新しい絵を描く準備をしています。'],
    [/^set_color\(/, 'これ以降の図形や点をどの色で描くかを指定し、見た目の区別が付くようにしています。'],
    [/^draw_line\(/, '指定した2点を結ぶ線を描画し、輪郭や軸、パーツ同士の接続を表現しています。'],
    [/^draw_rect\(/, '指定した位置と大きさで矩形を描き、背景やブロック、棒グラフの棒などを表現しています。'],
    [/^draw_circle\(/, '中心座標と半径をもとに円を描き、マークや顔、アニメーションの部品を作っています。'],
    [/^set_pixel\(/, '指定した1点だけを描画し、ドット絵や関数グラフのような細かな表現を行っています。'],
    [/^update_frame\(/, 'ここまでに積み上げた描画命令をキャンバスへ反映し、画面として見える状態に更新しています。'],
    [/^wait_msec\(/, '一定時間待機して描画更新の間隔を作り、動きの速さやアニメーションのテンポを調整しています。'],
    [/^get_key\(/, 'キーボード入力を1文字読み取り、プレイヤー操作や分岐条件に使う値を取得しています。'],
    [/^free\(/, '動的に確保したメモリを解放し、使い終わった領域を片付けています。'],
    [/^srand\(/, '乱数の初期値を設定し、毎回同じ並びになりすぎないようにしています。'],
    [/^merge_sort\(/, 'マージソート関数を呼び出して、配列全体を再帰的に整列させる処理を開始しています。'],
    [/^quick_sort\(/, 'クイックソート関数を呼び出し、基準値による分割と再帰を使って整列を進めています。'],
    [/^dfs\(/, '深さ優先探索の関数を呼び出し、グラフや木構造をたどる処理を開始しています。']
  ];

  const matched = callMap.find(([pattern]) => pattern.test(line));
  if (matched) {
    return matched[1];
  }

  const genericCall = line.match(/^([A-Za-z_]\w*)\((.*)\);$/);
  if (!genericCall) {
    return null;
  }

  const [, functionName] = genericCall;
  return `関数 \`${functionName}\` を呼び出して、この場面で必要な処理をまとまりとして実行しています。`;
};

const explainCodeLine = (line) => {
  if (line.startsWith('#include')) {
    return explainIncludeLine(line);
  }

  if (line.startsWith('#define ')) {
    return 'プリプロセッサマクロを定義し、後続のコードで定数や短い式を読みやすく再利用できるようにしています。';
  }

  if (/^typedef struct\b/.test(line)) {
    return '新しい構造体型を定義し始めており、関連する値をひとまとまりとして扱えるようにしています。';
  }

  if (/^struct\s+\w+\s*\{$/.test(line)) {
    return '構造体の型定義を始め、この後に並ぶメンバを1つのデータ型としてまとめようとしています。';
  }

  if (/^enum\s+\w+\s*\{$/.test(line)) {
    return '列挙型の定義を始め、意味のある整数定数をまとめて扱えるようにしています。';
  }

  if (/^union\s+\w+\s*\{$/.test(line)) {
    return '共用体の型定義を始め、同じメモリ領域を複数の型として使う準備をしています。';
  }

  if (/^\}?\s*else if\s*\(/.test(line)) {
    return '前の条件が成り立たなかった場合に、追加の条件を評価して処理の分岐先を細かく切り替えています。';
  }

  if (/^\}?\s*else\s*\{/.test(line)) {
    return 'それまでの条件がどれも当てはまらなかった場合の処理ブロックへ入ることを示しています。';
  }

  if (/^if\s*\(/.test(line)) {
    return '条件式を評価し、ここで指定した条件が真のときだけ次の処理を実行するようにしています。';
  }

  if (/^switch\s*\(/.test(line)) {
    return '1つの値をもとに複数の分岐先を選べるようにし、場合分けの流れを整理しています。';
  }

  if (/^case\s+/.test(line)) {
    return 'switch 文の中で、特定の値に一致したときに実行される処理の入口を表しています。';
  }

  if (/^default:/.test(line)) {
    return 'どの case にも一致しなかったときに実行する、最後の受け皿となる処理を示しています。';
  }

  if (/^for\s*\(/.test(line)) {
    return 'ループ変数を初期化し、条件が成り立つ間だけ処理を繰り返しながら、毎回少しずつ状態を更新しています。';
  }

  if (/^while\s*\(/.test(line)) {
    return '条件が真のあいだ同じ処理を繰り返し、入力待ちや探索、反復計算を続けられるようにしています。';
  }

  if (/^do\s*\{/.test(line)) {
    return 'まず本体を1回実行してから条件判定へ進む `do-while` ループを始めています。';
  }

  const functionDefinition = explainFunctionDefinitionLine(line);
  if (functionDefinition) {
    return functionDefinition;
  }

  const declarationExplanation = explainDeclarationLine(line);
  if (declarationExplanation) {
    return declarationExplanation;
  }

  const assignmentExplanation = explainAssignmentLine(line);
  if (assignmentExplanation) {
    return assignmentExplanation;
  }

  if (/^return\b/.test(line)) {
    if (/^return 0;?$/.test(line)) {
      return 'プログラムが正常に終了したことを呼び出し元へ知らせ、`main` 関数を終えています。';
    }
    return 'ここで計算結果を返すか、関数の処理を終了して呼び出し元へ制御を戻しています。';
  }

  const callExplanation = explainCallLine(line);
  if (callExplanation) {
    return callExplanation;
  }

  if (line.includes('->')) {
    return '構造体ポインタが指している先のメンバへアクセスし、連結データや階層データを扱っています。';
  }

  if (line === 'break;') {
    return '現在のループや switch 文をここで打ち切り、次の処理へ抜けるようにしています。';
  }

  if (line === 'continue;') {
    return 'この周回の残りを飛ばして、次のループ反復へすぐ進むようにしています。';
  }

  return 'この行で、このサンプルの目的に合わせて値や状態を更新し、次の処理が正しく動くように整えています。';
};

const buildCodeExplanation = (code, type) => {
  const meaningfulLines = code
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && line !== '{' && line !== '}' && line !== '};');

  const intro =
    type === 'graphics'
      ? 'このサンプルでは、描画命令を上から順に実行し、`update_frame()` を呼んだタイミングでキャンバスへ反映します。'
      : 'このサンプルでは、宣言・計算・分岐・関数呼び出しを上から順に実行しながら、テーマに沿った処理の流れを作っています。';

  return lines(
    '## サンプルプログラムの詳細解説',
    '',
    intro,
    '',
    ...meaningfulLines.map((line, index) => `${index + 1}. \`${line}\` - ${explainCodeLine(line)}`)
  );
};

const lesson = ({
  title,
  category,
  difficulty,
  description,
  points,
  code,
  type
}) => {
  const expandedDescription = buildLessonDescription({
    description,
    points,
    category,
    difficulty,
    type
  });

  return {
    title,
    category,
    difficulty,
    description: expandedDescription,
    type,
    content: lines(
      `# ${title}`,
      '',
      expandedDescription,
      '',
      '学習ポイント:',
      ...points.map((point, index) => `${index + 1}. ${point}`),
      '',
      buildCodeExplanation(code, type)
    ),
    code
  };
};

const graphicsProgram = ({ includes = ['#include "graphics.h"'], ...rest }) =>
  program({
    includes,
    ...rest
  });

const graphicsLesson = ({
  title,
  difficulty = '中級',
  description,
  points,
  code
}) =>
  lesson({
    title,
    category: 'グラフィックス',
    difficulty,
    description,
    points,
    code,
    type: 'graphics'
  });

const lessonSeed = [
  lesson({
    title: 'プログラムの基本構造',
    category: '基礎',
    difficulty: '初級',
    description: 'Cプログラムがどこから始まり、どう終わるのかを確認します。',
    points: ['`main` 関数から実行が始まる', '`printf` で文字列を表示できる', '`return 0;` で正常終了を示す'],
    code: program({
      mainBody: 'printf("Hello, C!\\n");'
    })
  }),
  lesson({
    title: 'コメントとコード整形',
    category: '基礎',
    difficulty: '初級',
    description: 'コメントの書き方と、読みやすいコード配置の基本を学びます。',
    points: ['`//` と `/* ... */` を使い分ける', 'インデントで処理のまとまりを見やすくする', 'コメントは意図を短く書く'],
    code: program({
      mainBody: `// 1行コメント
int score = 90;

/* 複数行コメントの例 */
printf("score = %d\\n", score);`
    })
  }),
  lesson({
    title: '変数の宣言と代入',
    category: '基礎',
    difficulty: '初級',
    description: '値を保存する変数の宣言と代入を確認します。',
    points: ['型と変数名をセットで書く', '代入で値を入れる', 'あとから値を書き換えられる'],
    code: program({
      mainBody: `int age = 18;
age = 19;
printf("age = %d\\n", age);`
    })
  }),
  lesson({
    title: '整数型の使い分け',
    category: '基礎',
    difficulty: '初級',
    description: '`int`、`long`、`short` などの整数型を整理します。',
    points: ['整数型ごとに扱える範囲が異なる', '`sizeof` でサイズを確認できる', '用途に応じて型を選ぶ'],
    code: program({
      mainBody: `short s = 12;
int i = 30000;
long l = 1000000L;

printf("%d %d %ld\\n", s, i, l);
printf("sizeof(int) = %zu\\n", sizeof(int));`
    })
  }),
  lesson({
    title: '浮動小数点型の使い分け',
    category: '基礎',
    difficulty: '初級',
    description: '`float` と `double` の違いを確認します。',
    points: ['小数を扱う型を区別する', '`double` の方が精度が高い', '表示桁数は書式指定で調整する'],
    code: program({
      mainBody: `float f = 3.14f;
double d = 3.1415926535;

printf("float  = %.2f\\n", f);
printf("double = %.10f\\n", d);`
    })
  }),
  lesson({
    title: '文字型とASCII',
    category: '基礎',
    difficulty: '初級',
    description: '`char` と文字コードの関係を学びます。',
    points: ['文字は内部では数値として扱われる', '`%c` と `%d` で見え方が変わる', '英字の並びを計算に使える'],
    code: program({
      mainBody: `char letter = 'A';

printf("文字: %c\\n", letter);
printf("ASCII: %d\\n", letter);
printf("次の文字: %c\\n", letter + 1);`
    })
  }),
  lesson({
    title: '定数とconst',
    category: '基礎',
    difficulty: '初級',
    description: '変更しない値を `const` で表す方法を学びます。',
    points: ['変えたくない値を明示できる', '意味のある名前を付ける', '誤代入の防止に役立つ'],
    code: program({
      mainBody: `const double TAX_RATE = 0.10;
double price = 1200.0;

printf("税込: %.0f円\\n", price * (1.0 + TAX_RATE));`
    })
  }),
  lesson({
    title: '算術演算子',
    category: '基礎',
    difficulty: '初級',
    description: '加減乗除と剰余の基本を確認します。',
    points: ['`+ - * / %` を使い分ける', '整数除算は小数点以下を切り捨てる', '剰余で余りを求められる'],
    code: program({
      mainBody: `int a = 10;
int b = 3;

printf("%d\\n", a + b);
printf("%d\\n", a - b);
printf("%d\\n", a * b);
printf("%d\\n", a / b);
printf("%d\\n", a % b);`
    })
  }),
  lesson({
    title: '代入演算子',
    category: '基礎',
    difficulty: '初級',
    description: '複合代入を使って更新処理を短く書く方法を学びます。',
    points: ['`+=` などで更新式を簡潔に書ける', '変数の現在値を使って更新する', 'ループ内の集計で多用する'],
    code: program({
      mainBody: `int value = 10;

value += 5;
value *= 2;
value -= 4;

printf("%d\\n", value);`
    })
  }),
  lesson({
    title: 'インクリメントとデクリメント',
    category: '基礎',
    difficulty: '初級',
    description: '1ずつ増減する演算子の動作を確認します。',
    points: ['`++` と `--` は1だけ更新する', '前置と後置で評価順が変わる', '単独文として使うと分かりやすい'],
    code: program({
      mainBody: `int count = 3;

count++;
printf("%d\\n", count);
count--;
printf("%d\\n", count);`
    })
  }),
  lesson({
    title: '比較演算子',
    category: '条件分岐',
    difficulty: '初級',
    description: '値同士を比較して条件式を作る方法を学びます。',
    points: ['`==`, `!=`, `<`, `>` を使う', '比較結果は条件分岐に使える', '`=` と `==` を混同しない'],
    code: program({
      mainBody: `int score = 75;

printf("%d\\n", score >= 60);
printf("%d\\n", score == 100);
printf("%d\\n", score != 0);`
    })
  }),
  lesson({
    title: '論理演算子',
    category: '条件分岐',
    difficulty: '初級',
    description: '複数の条件を組み合わせる方法を確認します。',
    points: ['`&&` は AND、`||` は OR', '`!` は条件を反転する', '条件をまとめると分岐が読みやすくなる'],
    code: program({
      mainBody: `int age = 20;
int hasTicket = 1;

printf("%d\\n", age >= 18 && hasTicket);
printf("%d\\n", age < 18 || !hasTicket);`
    })
  }),
  lesson({
    title: '演算子の優先順位',
    category: '基礎',
    difficulty: '初級',
    description: '式がどの順で評価されるかを整理します。',
    points: ['掛け算は足し算より先に計算される', 'かっこで優先順位を明示できる', '複雑な式は分けて書いてもよい'],
    code: program({
      mainBody: `int a = 2 + 3 * 4;
int b = (2 + 3) * 4;

printf("%d\\n", a);
printf("%d\\n", b);`
    })
  }),
  lesson({
    title: '明示的な型変換',
    category: '基礎',
    difficulty: '初級',
    description: 'キャストを使って計算結果の型を変える方法を学びます。',
    points: ['整数同士の割り算は整数になる', '`(double)` で型を変えられる', '必要な場所だけ変換する'],
    code: program({
      mainBody: `int total = 7;
int count = 2;

printf("整数除算: %d\\n", total / count);
printf("実数除算: %.2f\\n", (double)total / count);`
    })
  }),
  lesson({
    title: 'printfの書式指定',
    category: '入出力',
    difficulty: '初級',
    description: '`printf` で値の種類に応じた表示形式を使い分けます。',
    points: ['整数・小数・文字列で指定子が異なる', '桁数を制御できる', '複数の値をまとめて表示できる'],
    code: program({
      mainBody: `char name[] = "Rin";
int score = 88;
double rate = 93.456;

printf("%s %d %.1f\\n", name, score, rate);`
    })
  }),
  lesson({
    title: 'scanfで整数入力',
    category: '入出力',
    difficulty: '初級',
    description: '標準入力から整数を受け取る基本を確認します。',
    points: ['`scanf` では変数のアドレスを渡す', '入力待ちになることを理解する', '読み取った値をそのまま使える'],
    code: program({
      mainBody: `int age;

printf("年齢を入力してください: ");
scanf("%d", &age);
printf("%d歳です。\\n", age);`
    })
  }),
  lesson({
    title: 'scanfで複数入力',
    category: '入出力',
    difficulty: '初級',
    description: '複数の値を1回の入力で受け取る方法を学びます。',
    points: ['書式文字列に複数の指定子を書く', '空白区切りで入力できる', '複数値の計算へすぐ使える'],
    code: program({
      mainBody: `int a;
int b;

scanf("%d %d", &a, &b);
printf("sum = %d\\n", a + b);`
    })
  }),
  lesson({
    title: 'if文',
    category: '条件分岐',
    difficulty: '初級',
    description: '条件が真のときだけ処理を実行する分岐を学びます。',
    points: ['真のときだけブロックが実行される', '条件式は0以外で真とみなされる', '単純な分岐に向いている'],
    code: program({
      mainBody: `int temperature = 30;

if (temperature >= 28) {
    printf("今日は暑いです。\\n");
}`
    })
  }),
  lesson({
    title: 'if-else文',
    category: '条件分岐',
    difficulty: '初級',
    description: '条件に応じて2通りの処理を切り替える方法を学びます。',
    points: ['真のときと偽のときの処理を分ける', '必ずどちらか一方が実行される', '単純な判定に向いている'],
    code: program({
      mainBody: `int score = 55;

if (score >= 60) {
    printf("合格\\n");
} else {
    printf("再挑戦\\n");
}`
    })
  }),
  lesson({
    title: 'else-if連鎖',
    category: '条件分岐',
    difficulty: '初級',
    description: '複数条件を上から順に判定する方法を学びます。',
    points: ['最初に一致した分岐だけ実行される', '条件の順序が結果に影響する', '評価や料金表に向いている'],
    code: program({
      mainBody: `int score = 82;

if (score >= 90) {
    printf("A\\n");
} else if (score >= 80) {
    printf("B\\n");
} else if (score >= 70) {
    printf("C\\n");
} else {
    printf("D\\n");
}`
    })
  }),
  lesson({
    title: 'switch文',
    category: '条件分岐',
    difficulty: '初級',
    description: '値ごとに処理を分岐する `switch` 文を学びます。',
    points: ['整数や文字で分岐できる', '`break` を忘れると次の分岐へ進む', '`default` で想定外の値を扱える'],
    code: program({
      mainBody: `int menu = 2;

switch (menu) {
    case 1:
        printf("追加\\n");
        break;
    case 2:
        printf("更新\\n");
        break;
    default:
        printf("終了\\n");
        break;
}`
    })
  }),
  lesson({
    title: '条件演算子',
    category: '条件分岐',
    difficulty: '中級',
    description: '短い条件分岐を1つの式として書く方法を学びます。',
    points: ['`条件 ? 真の値 : 偽の値` の形で書く', '短い代入処理で便利', '複雑な分岐では無理に使わない'],
    code: program({
      mainBody: `int score = 73;
const char *result = score >= 60 ? "合格" : "不合格";

printf("%s\\n", result);`
    })
  }),
  lesson({
    title: 'while文',
    category: 'ループ',
    difficulty: '初級',
    description: '条件が真の間だけ繰り返す基本ループを学びます。',
    points: ['条件を先に判定する', '更新を忘れると無限ループになる', '回数未定の処理に向いている'],
    code: program({
      mainBody: `int i = 1;

while (i <= 5) {
    printf("%d\\n", i);
    i++;
}`
    })
  }),
  lesson({
    title: 'do-while文',
    category: 'ループ',
    difficulty: '初級',
    description: '最低1回は本体を実行するループを学びます。',
    points: ['本体を先に実行してから条件を判定する', '再入力処理と相性がよい', '末尾の `;` を忘れない'],
    code: program({
      mainBody: `int count = 3;

do {
    printf("%d\\n", count);
    count--;
} while (count > 0);`
    })
  }),
  lesson({
    title: 'for文',
    category: 'ループ',
    difficulty: '初級',
    description: '回数が決まっている繰り返しを簡潔に書く方法を学びます。',
    points: ['初期化・条件・更新を1行にまとめる', '配列処理で多用する', '開始値と終了条件を丁寧に確認する'],
    code: program({
      mainBody: `for (int i = 1; i <= 5; i++) {
    printf("%d\\n", i);
}`
    })
  }),
  lesson({
    title: 'ネストしたループ',
    category: 'ループ',
    difficulty: '初級',
    description: 'ループの中にループを入れて表やパターンを作る方法を学びます。',
    points: ['外側が行、内側が列という考え方ができる', '2重配列の走査でも使う', '変数名を分けて役割を明確にする'],
    code: program({
      mainBody: `for (int row = 1; row <= 3; row++) {
    for (int col = 1; col <= 4; col++) {
        printf("*");
    }
    printf("\\n");
}`
    })
  }),
  lesson({
    title: 'break文',
    category: 'ループ',
    difficulty: '初級',
    description: 'ループを途中で終了する方法を学びます。',
    points: ['条件成立時に即終了できる', '探索処理と相性がよい', '終了条件をコードで明示できる'],
    code: program({
      mainBody: `for (int i = 1; i <= 10; i++) {
    if (i == 6) {
        break;
    }
    printf("%d\\n", i);
}`
    })
  }),
  lesson({
    title: 'continue文',
    category: 'ループ',
    difficulty: '初級',
    description: '現在の周回だけ処理を飛ばして次へ進む方法を学びます。',
    points: ['1回分だけ残りの処理を飛ばせる', '除外条件の表現に便利', '使いすぎると読みにくくなる'],
    code: program({
      mainBody: `for (int i = 1; i <= 8; i++) {
    if (i % 2 == 0) {
        continue;
    }
    printf("%d\\n", i);
}`
    })
  }),
  lesson({
    title: '番兵値を使うループ',
    category: 'ループ',
    difficulty: '中級',
    description: '特定の値が入力されたら終了するループ設計を学びます。',
    points: ['終了用の特別な値を決める', '入力処理とループ条件を組み合わせる', '対話的なプログラムでよく使う'],
    code: program({
      mainBody: `int value = 1;

while (value != 0) {
    printf("値を入力してください(0で終了): ");
    scanf("%d", &value);
    printf("入力値: %d\\n", value);
}`
    })
  }),
  lesson({
    title: '関数の宣言と定義',
    category: '関数',
    difficulty: '初級',
    description: '処理を関数に切り出す基本形を学びます。',
    points: ['関数に役割を持たせて再利用する', '`main` 以外の処理を分離できる', '名前で意図を伝えられる'],
    code: program({
      helpers: `void greet(void) {
    printf("ようこそCへ。\\n");
}`,
      mainBody: `greet();
greet();`
    })
  }),
  lesson({
    title: '関数プロトタイプ',
    category: '関数',
    difficulty: '初級',
    description: '関数の実装より前に宣言だけを書く理由を学びます。',
    points: ['呼び出し前に存在を伝えられる', '大きなプログラムで役立つ', '戻り値や引数の型を先に決められる'],
    code: program({
      helpers: `int add(int a, int b);

int add(int a, int b) {
    return a + b;
}`,
      mainBody: `printf("%d\\n", add(4, 7));`
    })
  }),
  lesson({
    title: '引数と戻り値',
    category: '関数',
    difficulty: '初級',
    description: '入力を受け取り結果を返す関数の基本を学びます。',
    points: ['引数は関数への入力', '戻り値は結果の受け渡し', '計算処理を部品化できる'],
    code: program({
      helpers: `double average(int a, int b) {
    return (a + b) / 2.0;
}`,
      mainBody: `printf("%.1f\\n", average(7, 10));`
    })
  }),
  lesson({
    title: 'void関数',
    category: '関数',
    difficulty: '初級',
    description: '値を返さず処理だけ行う関数の使い方を学びます。',
    points: ['戻り値が不要な処理で使う', '表示や初期化処理に向いている', '`return` は省略できる'],
    code: program({
      helpers: `void print_border(void) {
    printf("----------\\n");
}`,
      mainBody: `print_border();
printf("menu\\n");
print_border();`
    })
  }),
  lesson({
    title: '値渡しの理解',
    category: '関数',
    difficulty: '初級',
    description: '関数に値を渡したとき、元の変数がどうなるかを確認します。',
    points: ['通常の引数は値渡しになる', '関数内での変更は元の変数へ影響しない', '後でポインタ渡しと比較すると理解しやすい'],
    code: program({
      helpers: `void add_one(int x) {
    x++;
    printf("関数内: %d\\n", x);
}`,
      mainBody: `int value = 10;
add_one(value);
printf("関数外: %d\\n", value);`
    })
  }),
  lesson({
    title: 'グローバル変数とローカル変数',
    category: '関数',
    difficulty: '中級',
    description: '変数の見える範囲と寿命の違いを整理します。',
    points: ['ローカル変数は関数内だけで有効', 'グローバル変数は複数関数から参照できる', '多用しすぎると追跡しにくい'],
    code: program({
      helpers: `int total = 0;

void add_score(int score) {
    total += score;
}`,
      mainBody: `add_score(20);
add_score(30);
printf("%d\\n", total);`
    })
  }),
  lesson({
    title: 'staticローカル変数',
    category: '関数',
    difficulty: '中級',
    description: '関数を抜けても値を保持するローカル変数を学びます。',
    points: ['初期化は最初の1回だけ行われる', '呼び出し回数の記録に使える', '状態を持つ関数を作れる'],
    code: program({
      helpers: `void counter(void) {
    static int count = 0;
    count++;
    printf("%d\\n", count);
}`,
      mainBody: `counter();
counter();
counter();`
    })
  }),
  lesson({
    title: '再帰の基本',
    category: '関数',
    difficulty: '中級',
    description: '関数が自分自身を呼び出す再帰の考え方を学びます。',
    points: ['終了条件を最初に決める', '問題を小さくして繰り返す', '呼び出しの流れを紙で追うと分かりやすい'],
    code: program({
      helpers: `int factorial(int n) {
    if (n <= 1) {
        return 1;
    }
    return n * factorial(n - 1);
}`,
      mainBody: `printf("%d\\n", factorial(5));`
    })
  }),
  lesson({
    title: 'ユークリッドの互除法',
    category: '関数',
    difficulty: '中級',
    description: '最大公約数を効率よく求める再帰の例を学びます。',
    points: ['`a % b` を使って問題を縮小する', '再帰と数学の相性がよい', '終了条件は `b == 0` で考える'],
    code: program({
      helpers: `int gcd(int a, int b) {
    if (b == 0) {
        return a;
    }
    return gcd(b, a % b);
}`,
      mainBody: `printf("%d\\n", gcd(84, 30));`
    })
  }),
  lesson({
    title: '関数分割による設計',
    category: '関数',
    difficulty: '中級',
    description: '大きな処理を小さな関数へ分ける設計の基本を学びます。',
    points: ['1関数1役割を意識する', '組み合わせやすい部品を作る', '読みやすさと再利用性が上がる'],
    code: program({
      helpers: `int square(int x) {
    return x * x;
}

int add(int a, int b) {
    return a + b;
}`,
      mainBody: `int total = add(square(2), square(3));
printf("%d\\n", total);`
    })
  }),
  lesson({
    title: '一次元配列',
    category: '配列',
    difficulty: '初級',
    description: '同じ型の値を連続して扱う一次元配列を学びます。',
    points: ['添字は0から始まる', '配列サイズを超えてアクセスしない', '複数データをまとめて扱える'],
    code: program({
      mainBody: `int scores[4] = {70, 80, 90, 100};

printf("%d\\n", scores[0]);
printf("%d\\n", scores[3]);`
    })
  }),
  lesson({
    title: '配列の初期化',
    category: '配列',
    difficulty: '初級',
    description: '宣言と同時に複数要素を初期化する方法を学びます。',
    points: ['波かっこで値を並べる', '省略時は先頭から順に入る', '要素数の省略もできる'],
    code: program({
      mainBody: `int values[] = {3, 6, 9, 12};

for (int i = 0; i < 4; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  lesson({
    title: '配列の集計',
    category: '配列',
    difficulty: '初級',
    description: '配列全体を走査して合計や平均を求める方法を学びます。',
    points: ['`for` 文で全要素へアクセスする', '集計用変数を用意する', '平均は実数除算を意識する'],
    code: program({
      mainBody: `int values[5] = {4, 8, 15, 16, 23};
int total = 0;

for (int i = 0; i < 5; i++) {
    total += values[i];
}

printf("sum = %d\\n", total);`
    })
  }),
  lesson({
    title: '最大値と最小値の探索',
    category: '配列',
    difficulty: '初級',
    description: '配列から最大値や最小値を見つける基本パターンを学びます。',
    points: ['最初の要素を基準値にする', '1件ずつ比較して更新する', '探索処理の基本形になる'],
    code: program({
      mainBody: `int values[5] = {12, 5, 19, 7, 14};
int max = values[0];
int min = values[0];

for (int i = 1; i < 5; i++) {
    if (values[i] > max) {
        max = values[i];
    }
    if (values[i] < min) {
        min = values[i];
    }
}

printf("%d %d\\n", max, min);`
    })
  }),
  lesson({
    title: '二次元配列',
    category: '配列',
    difficulty: '中級',
    description: '表形式のデータを扱う二次元配列を学びます。',
    points: ['行と列の2つの添字を持つ', '外側と内側のループを使い分ける', '表や盤面の表現に向いている'],
    code: program({
      mainBody: `int table[2][3] = {
    {1, 2, 3},
    {4, 5, 6}
};

printf("%d\\n", table[1][2]);`
    })
  }),
  lesson({
    title: '行列の走査',
    category: '配列',
    difficulty: '中級',
    description: '二次元配列を二重ループで順に処理する方法を学びます。',
    points: ['行ごと・列ごとの処理を分けて考える', '集計にも二重ループが使える', '座標の意味を意識する'],
    code: program({
      mainBody: `int matrix[2][2] = {
    {1, 2},
    {3, 4}
};

for (int row = 0; row < 2; row++) {
    for (int col = 0; col < 2; col++) {
        printf("%d ", matrix[row][col]);
    }
    printf("\\n");
}`
    })
  }),
  lesson({
    title: '文字列の基本',
    category: '文字列',
    difficulty: '初級',
    description: 'Cの文字列が `char` 配列で表現される仕組みを学びます。',
    points: ['末尾に終端文字 `\\0` が入る', '1文字ずつ配列として扱える', '文字列リテラルで初期化できる'],
    code: program({
      mainBody: `char text[] = "Hello";

printf("%s\\n", text);
printf("%c\\n", text[1]);`
    })
  }),
  lesson({
    title: '文字列長の計算',
    category: '文字列',
    difficulty: '初級',
    description: '終端文字まで数えることで文字列長を求める方法を学びます。',
    points: ['`\\0` が出るまで数える', '配列走査の練習になる', '`strlen` の動作理解にもつながる'],
    code: program({
      mainBody: `char word[] = "program";
int length = 0;

while (word[length] != '\\0') {
    length++;
}

printf("%d\\n", length);`
    })
  }),
  lesson({
    title: 'string.hの活用',
    category: '文字列',
    difficulty: '中級',
    description: '`string.h` の基本関数で文字列処理を効率化します。',
    points: ['`strlen`、`strcpy`、`strcat` を知る', '配列サイズに注意する', 'ライブラリ関数を活用する'],
    code: program({
      includes: ['#include <stdio.h>', '#include <string.h>'],
      mainBody: `char text[20] = "C";

strcat(text, " Lang");
printf("%s\\n", text);
printf("%zu\\n", strlen(text));`
    })
  }),
  lesson({
    title: '文字分類と変換',
    category: '文字列',
    difficulty: '中級',
    description: '`ctype.h` を使って文字種の判定や大文字変換を行います。',
    points: ['英字か数字かを判定できる', '大文字・小文字変換に使える', '文字入力処理で役立つ'],
    code: program({
      includes: ['#include <stdio.h>', '#include <ctype.h>'],
      mainBody: `char ch = 'a';

printf("%d\\n", isalpha((unsigned char)ch));
printf("%c\\n", toupper((unsigned char)ch));`
    })
  }),
  lesson({
    title: '文字列配列',
    category: '文字列',
    difficulty: '中級',
    description: '複数の単語を配列としてまとめる方法を学びます。',
    points: ['2次元の `char` 配列で表現できる', '単語一覧の管理に使える', '添字で単語を選べる'],
    code: program({
      mainBody: `char words[3][10] = {"cat", "dog", "bird"};

for (int i = 0; i < 3; i++) {
    printf("%s\\n", words[i]);
}`
    })
  }),
  lesson({
    title: 'コマンドライン引数',
    category: '文字列',
    difficulty: '中級',
    description: '`main` の引数で起動時の文字列を受け取る方法を学びます。',
    points: ['`argc` は個数、`argv` は文字列配列', '外部から設定を渡せる', 'CLIツールの基本になる'],
    code: program({
      mainSignature: 'int main(int argc, char *argv[])',
      mainBody: `printf("argc = %d\\n", argc);

for (int i = 0; i < argc; i++) {
    printf("%s\\n", argv[i]);
}`,
      addReturn: true
    })
  }),
  lesson({
    title: 'ポインタの基礎',
    category: 'ポインタ',
    difficulty: '中級',
    description: '変数のアドレスを扱うポインタの基本概念を学びます。',
    points: ['ポインタはアドレスを保存する', '`&` でアドレスを取得する', '`*` で指す先の値を読む'],
    code: program({
      mainBody: `int value = 42;
int *ptr = &value;

printf("%p\\n", (void *)ptr);
printf("%d\\n", *ptr);`
    })
  }),
  lesson({
    title: 'アドレス演算子と間接参照',
    category: 'ポインタ',
    difficulty: '中級',
    description: '`&` と `*` を使って値とアドレスを行き来する方法を確認します。',
    points: ['値そのものとアドレスを区別する', '間接参照で値を書き換えられる', 'ポインタ操作の最初の一歩になる'],
    code: program({
      mainBody: `int score = 80;
int *ptr = &score;

*ptr = 95;
printf("%d\\n", score);`
    })
  }),
  lesson({
    title: 'ポインタと配列',
    category: 'ポインタ',
    difficulty: '中級',
    description: '配列名とポインタの関係を学びます。',
    points: ['配列名は先頭要素のアドレスとして扱える', '`array[i]` と `*(array + i)` は対応する', '関数へ配列を渡す基礎になる'],
    code: program({
      mainBody: `int values[4] = {10, 20, 30, 40};

printf("%d\\n", *values);
printf("%d\\n", *(values + 2));`
    })
  }),
  lesson({
    title: 'ポインタ演算',
    category: 'ポインタ',
    difficulty: '中級',
    description: 'ポインタを進めて配列要素を順番に参照する方法を学びます。',
    points: ['型のサイズ分だけ進む', '配列走査で使える', '範囲外アクセスは避ける'],
    code: program({
      mainBody: `int values[3] = {5, 10, 15};
int *ptr = values;

for (int i = 0; i < 3; i++) {
    printf("%d\\n", *(ptr + i));
}`
    })
  }),
  lesson({
    title: '関数へのポインタ渡し',
    category: 'ポインタ',
    difficulty: '中級',
    description: '関数の中から呼び出し元の値を変更する方法を学びます。',
    points: ['値渡しと違って元の変数を更新できる', '出力引数としても使える', 'CらしいAPI設計でよく登場する'],
    code: program({
      helpers: `void add_bonus(int *score) {
    *score += 10;
}`,
      mainBody: `int score = 70;
add_bonus(&score);
printf("%d\\n", score);`
    })
  }),
  lesson({
    title: 'swapの実装',
    category: 'ポインタ',
    difficulty: '中級',
    description: 'ポインタを使って2つの値を交換する定番処理を学びます。',
    points: ['交換には一時変数が必要', 'アドレスを渡して元の変数を更新する', 'ポインタ利用の代表例になる'],
    code: program({
      helpers: `void swap(int *a, int *b) {
    int temp = *a;
    *a = *b;
    *b = temp;
}`,
      mainBody: `int x = 3;
int y = 8;

swap(&x, &y);
printf("%d %d\\n", x, y);`
    })
  }),
  lesson({
    title: 'charポインタと文字列',
    category: 'ポインタ',
    difficulty: '中級',
    description: '文字列を `char *` として扱う場面を確認します。',
    points: ['文字列リテラルは先頭文字へのポインタで扱える', '1文字ずつ進めて読める', '書き換え可否は配列と違う点に注意する'],
    code: program({
      mainBody: `char *text = "pointer";

while (*text != '\\0') {
    printf("%c\\n", *text);
    text++;
}`
    })
  }),
  lesson({
    title: 'malloc',
    category: 'メモリ',
    difficulty: '中級',
    description: '実行時に必要なだけメモリを確保する基本を学びます。',
    points: ['必要サイズを `sizeof` と組み合わせて計算する', '失敗時は `NULL` を確認する', '使い終わったら `free` する'],
    code: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int count = 4;
int *values = malloc(count * sizeof(int));

if (values == NULL) {
    return 1;
}

for (int i = 0; i < count; i++) {
    values[i] = (i + 1) * 10;
    printf("%d\\n", values[i]);
}

free(values);`,
      addReturn: true
    })
  }),
  lesson({
    title: 'callocとfree',
    category: 'メモリ',
    difficulty: '中級',
    description: '0初期化付き確保と解放の流れを確認します。',
    points: ['`calloc` は要素数と要素サイズで確保する', '初期値が0になる', '最後に `free` で解放する'],
    code: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *counts = calloc(5, sizeof(int));

if (counts == NULL) {
    return 1;
}

counts[2] = 7;
printf("%d\\n", counts[2]);
free(counts);`,
      addReturn: true
    })
  }),
  lesson({
    title: 'realloc',
    category: 'メモリ',
    difficulty: '中級',
    description: '動的配列のサイズを後から変更する方法を学びます。',
    points: ['既存ポインタを拡張・縮小できる', '返り値を受け取り直す', '失敗時に元ポインタを失わない設計が大切'],
    code: program({
      includes: ['#include <stdio.h>', '#include <stdlib.h>'],
      mainBody: `int *values = malloc(2 * sizeof(int));
int *temp;

if (values == NULL) {
    return 1;
}

values[0] = 10;
values[1] = 20;
temp = realloc(values, 4 * sizeof(int));
if (temp == NULL) {
    free(values);
    return 1;
}

values = temp;
values[2] = 30;
values[3] = 40;
printf("%d\\n", values[3]);
free(values);`,
      addReturn: true
    })
  }),
  lesson({
    title: 'ポインタの配列と配列へのポインタ',
    category: 'ポインタ',
    difficulty: '上級',
    description: '似ている構文の意味の違いを整理します。',
    points: ['`int *a[3]` はポインタを並べた配列', '`int (*p)[3]` は配列全体を指すポインタ', '括弧の位置で意味が変わる'],
    code: program({
      mainBody: `int x = 10;
int y = 20;
int *ptrs[2] = {&x, &y};
int values[2][3] = {{1, 2, 3}, {4, 5, 6}};
int (*row)[3] = values;

printf("%d\\n", *ptrs[1]);
printf("%d\\n", row[1][2]);`
    })
  }),
  lesson({
    title: '二重ポインタ',
    category: 'ポインタ',
    difficulty: '上級',
    description: 'ポインタを指すポインタの用途を確認します。',
    points: ['ポインタそのものを書き換えるときに役立つ', '文字列配列や動的2次元配列でも登場する', '`**` の意味を段階的に追う'],
    code: program({
      mainBody: `int value = 50;
int *ptr = &value;
int **pptr = &ptr;

**pptr = 75;
printf("%d\\n", value);`
    })
  }),
  lesson({
    title: '関数ポインタ',
    category: 'ポインタ',
    difficulty: '上級',
    description: '関数そのものを変数のように扱う方法を学びます。',
    points: ['処理を引数として渡せる', '比較関数やコールバックで使う', '宣言が複雑なので型を丁寧に読む'],
    code: program({
      helpers: `int add(int a, int b) {
    return a + b;
}`,
      mainBody: `int (*calc)(int, int) = add;
printf("%d\\n", calc(4, 9));`
    })
  }),
  lesson({
    title: 'voidポインタ',
    category: 'ポインタ',
    difficulty: '上級',
    description: '任意の型のアドレスを受け取れる `void *` を学びます。',
    points: ['型情報を持たない汎用ポインタ', '使う前に元の型へ戻す', '標準ライブラリでも多く使われる'],
    code: program({
      mainBody: `int value = 123;
void *ptr = &value;

printf("%d\\n", *(int *)ptr);`
    })
  }),
  lesson({
    title: 'sizeofとメモリレイアウト',
    category: 'メモリ',
    difficulty: '中級',
    description: '型や配列がメモリ上でどのくらいの大きさを持つかを確認します。',
    points: ['型サイズは環境で異なることがある', '配列全体サイズから要素数を求められる', '構造体では詰め物が入る場合がある'],
    code: program({
      helpers: `struct Item {
    char code;
    int price;
};`,
      mainBody: `int values[5] = {0};

printf("%zu\\n", sizeof(values));
printf("%zu\\n", sizeof(struct Item));`
    })
  }),
  lesson({
    title: 'ビット表現の基礎',
    category: 'ビット演算',
    difficulty: '中級',
    description: '整数が2進数で表されることを前提にビット処理へ入ります。',
    points: ['各ビットは0か1を表す', '2進数の桁ごとに意味を持たせられる', 'フラグ管理や低水準処理の基礎になる'],
    code: program({
      mainBody: `unsigned int value = 13;

printf("%u\\n", value);
printf("%u\\n", value >> 1);`
    })
  }),
  lesson({
    title: 'ビットAND・OR・XOR',
    category: 'ビット演算',
    difficulty: '中級',
    description: '代表的なビット演算子3種の動きを確認します。',
    points: ['AND は共通ビットだけ残す', 'OR はどちらかが1なら立つ', 'XOR は差分ビットを取る'],
    code: program({
      mainBody: `unsigned int a = 6;
unsigned int b = 10;

printf("%u\\n", a & b);
printf("%u\\n", a | b);
printf("%u\\n", a ^ b);`
    })
  }),
  lesson({
    title: 'ビットシフト',
    category: 'ビット演算',
    difficulty: '中級',
    description: 'ビット列を左右にずらすシフト演算を学びます。',
    points: ['左シフトは桁を増やすイメージになる', '右シフトは桁を減らすイメージになる', 'ビット単位の高速処理に使われる'],
    code: program({
      mainBody: `unsigned int value = 3;

printf("%u\\n", value << 2);
printf("%u\\n", value >> 1);`
    })
  }),
  lesson({
    title: 'ビットマスク',
    category: 'ビット演算',
    difficulty: '中級',
    description: '特定ビットだけを取り出したり立てたりするパターンを学びます。',
    points: ['必要なビットだけ1にしたマスクを使う', '確認・設定・解除の基本形がある', '権限や状態の表現に向いている'],
    code: program({
      mainBody: `unsigned int flags = 0;

flags |= 1u << 2;
printf("%u\\n", flags);
printf("%u\\n", (flags & (1u << 2)) != 0);`
    })
  }),
  lesson({
    title: 'フラグ集合の設計',
    category: 'ビット演算',
    difficulty: '中級',
    description: '複数状態を1つの整数へ詰め込む設計パターンを学びます。',
    points: ['各ビットに役割を割り当てる', '設定・解除・確認のAPIを決める', 'メモリ効率の高い状態管理ができる'],
    code: program({
      helpers: `enum Permission {
    READ_FLAG = 1 << 0,
    WRITE_FLAG = 1 << 1,
    EXEC_FLAG = 1 << 2
};`,
      mainBody: `unsigned int perms = READ_FLAG | WRITE_FLAG;

printf("%d\\n", (perms & WRITE_FLAG) != 0);
perms &= ~WRITE_FLAG;
printf("%d\\n", (perms & WRITE_FLAG) != 0);`
    })
  }),
  lesson({
    title: 'ビットフィールド',
    category: 'ビット演算',
    difficulty: '上級',
    description: '構造体のメンバにビット幅を持たせる方法を学びます。',
    points: ['少ないビット数で値を保持できる', 'ハードウェアレジスタ表現で使われる', '実装依存の面もあるので注意する'],
    code: program({
      helpers: `struct Packet {
    unsigned int version : 3;
    unsigned int type : 5;
};`,
      mainBody: `struct Packet packet = {5, 17};

printf("%u %u\\n", packet.version, packet.type);`
    })
  }),
  lesson({
    title: '2の補数と符号表現',
    category: 'ビット演算',
    difficulty: '上級',
    description: '負数がどのようにビット列で表されるかを理解します。',
    points: ['負数は2の補数で表現される', '符号付きと符号なしで解釈が変わる', 'ビット演算の結果理解に重要な知識になる'],
    code: program({
      mainBody: `int signed_value = -5;
unsigned int raw = (unsigned int)signed_value;

printf("%d\\n", signed_value);
printf("%u\\n", raw);`
    })
  }),
  lesson({
    title: '固定小数点の考え方',
    category: 'ビット演算',
    difficulty: '上級',
    description: '整数を使って小数を近似する固定小数点表現を学びます。',
    points: ['一定倍率を掛けて整数化する', '組み込み処理で使われることがある', '丸め誤差の扱いを意識する'],
    code: program({
      mainBody: `int scale = 1000;
int a = 1250;
int b = 250;
int result = a + b;

printf("%.3f\\n", result / (double)scale);`
    })
  }),
  lesson({
    title: 'ビット列の1の個数',
    category: 'ビット演算',
    difficulty: '上級',
    description: '立っているビット数を数える popcount の基本を学びます。',
    points: ['ループで1ビットずつ確認できる', 'マスクとシフトを組み合わせる', '集合サイズや圧縮表現で役立つ'],
    code: program({
      mainBody: `unsigned int value = 29;
int count = 0;

while (value != 0) {
    count += value & 1u;
    value >>= 1;
}

printf("%d\\n", count);`
    })
  }),
  lesson({
    title: '構造体の基本',
    category: 'データ構造',
    difficulty: '中級',
    description: '異なる型の値を1つにまとめる構造体を学びます。',
    points: ['関連する値を1まとまりで扱える', 'メンバ名で意味を表せる', '現実の情報を表現しやすい'],
    code: program({
      helpers: `struct Student {
    char name[20];
    int score;
};`,
      mainBody: `struct Student s = {"Aki", 88};

printf("%s %d\\n", s.name, s.score);`
    })
  }),
  lesson({
    title: 'ネストした構造体',
    category: 'データ構造',
    difficulty: '中級',
    description: '構造体の中に別の構造体を入れて情報を階層化します。',
    points: ['複雑な情報を自然に表現できる', '関連項目をグループ化できる', '設計段階で型を分けると見通しがよくなる'],
    code: program({
      helpers: `struct Point {
    int x;
    int y;
};

struct Circle {
    struct Point center;
    int radius;
};`,
      mainBody: `struct Circle c = {{10, 20}, 15};

printf("%d %d %d\\n", c.center.x, c.center.y, c.radius);`
    })
  }),
  lesson({
    title: 'typedefとenum',
    category: 'データ構造',
    difficulty: '中級',
    description: '型名の別名や意味のある定数群の表現方法を学びます。',
    points: ['`typedef` で型名を短くできる', '`enum` で状態を意味付きの整数にできる', '可読性が上がる'],
    code: program({
      helpers: `typedef struct {
    int x;
    int y;
} Point;

enum State {
    IDLE,
    RUNNING,
    STOPPED
};`,
      mainBody: `Point p = {3, 4};
enum State state = RUNNING;

printf("%d %d %d\\n", p.x, p.y, state);`
    })
  }),
  lesson({
    title: 'union',
    category: 'データ構造',
    difficulty: '上級',
    description: '同じメモリ領域を複数の型で共有する union を学びます。',
    points: ['最後に書き込んだメンバが有効になる', 'メモリ節約や低水準データ解釈で使う', '構造体との違いを意識する'],
    code: program({
      helpers: `union Number {
    int i;
    float f;
};`,
      mainBody: `union Number value;

value.f = 12.5f;
printf("%.1f\\n", value.f);
printf("%d\\n", value.i);`
    })
  }),
  lesson({
    title: '単方向リスト',
    category: 'データ構造',
    difficulty: '上級',
    description: 'ポインタで要素をつなぐ単方向リストの基本を学びます。',
    points: ['各ノードが次ノードへのポインタを持つ', '挿入や削除の概念を理解する', '動的データ構造の土台になる'],
    code: program({
      helpers: `struct Node {
    int value;
    struct Node *next;
};`,
      mainBody: `struct Node third = {30, NULL};
struct Node second = {20, &third};
struct Node first = {10, &second};
struct Node *current = &first;

while (current != NULL) {
    printf("%d\\n", current->value);
    current = current->next;
}`
    })
  }),
  lesson({
    title: '双方向リスト',
    category: 'データ構造',
    difficulty: '上級',
    description: '前後両方向へたどれる双方向リストの構造を学びます。',
    points: ['前ノードと次ノードの両方を持つ', '前後移動がしやすい', 'ポインタ更新箇所が増えるので注意する'],
    code: program({
      helpers: `struct Node {
    int value;
    struct Node *prev;
    struct Node *next;
};`,
      mainBody: `struct Node first = {10, NULL, NULL};
struct Node second = {20, &first, NULL};

first.next = &second;
printf("%d %d\\n", first.next->value, second.prev->value);`
    })
  }),
  lesson({
    title: 'スタック',
    category: 'データ構造',
    difficulty: '中級',
    description: '後入れ先出しのスタック構造を配列で実装します。',
    points: ['push と pop の2操作が中心', '関数呼び出しや式評価でも使われる', '添字で先頭位置を管理できる'],
    code: program({
      mainBody: `int stack[5];
int top = 0;

stack[top++] = 10;
stack[top++] = 20;
printf("%d\\n", stack[--top]);`
    })
  }),
  lesson({
    title: 'キュー',
    category: 'データ構造',
    difficulty: '中級',
    description: '先入れ先出しのキュー構造を学びます。',
    points: ['enqueue と dequeue を行う', '待ち行列や幅優先探索で使う', '先頭と末尾を別に管理する'],
    code: program({
      mainBody: `int queue[5];
int front = 0;
int rear = 0;

queue[rear++] = 10;
queue[rear++] = 20;
printf("%d\\n", queue[front++]);`
    })
  }),
  lesson({
    title: 'リングバッファ',
    category: 'データ構造',
    difficulty: '上級',
    description: '配列を循環利用するリングバッファの考え方を学びます。',
    points: ['インデックスを剰余で循環させる', '固定長キューとして使える', '組み込み処理やストリーム処理で役立つ'],
    code: program({
      mainBody: `int buffer[4] = {0};
int head = 0;
int tail = 0;

buffer[tail] = 11;
tail = (tail + 1) % 4;
buffer[tail] = 22;
tail = (tail + 1) % 4;

printf("%d\\n", buffer[head]);
head = (head + 1) % 4;
printf("%d\\n", buffer[head]);`
    })
  }),
  lesson({
    title: '二分探索木',
    category: 'データ構造',
    difficulty: '上級',
    description: '大小関係で左右に分岐する木構造の基本を学びます。',
    points: ['左が小、右が大という規則を持つ', '探索を効率化できる', '再帰関数との相性がよい'],
    code: program({
      helpers: `struct Node {
    int value;
    struct Node *left;
    struct Node *right;
};`,
      mainBody: `struct Node left = {5, NULL, NULL};
struct Node right = {15, NULL, NULL};
struct Node root = {10, &left, &right};

printf("%d %d %d\\n", root.value, root.left->value, root.right->value);`
    })
  }),
  lesson({
    title: 'ヒープ',
    category: 'データ構造',
    difficulty: '上級',
    description: '優先順位付きデータを扱うヒープの基本を確認します。',
    points: ['親子関係を配列で表現できる', '最大値や最小値を素早く取り出せる', '優先度付きキューの基盤になる'],
    code: program({
      mainBody: `int heap[5] = {100, 80, 60, 30, 20};

printf("root = %d\\n", heap[0]);
printf("left child = %d\\n", heap[1]);
printf("right child = %d\\n", heap[2]);`
    })
  }),
  lesson({
    title: 'グラフ表現',
    category: 'データ構造',
    difficulty: '上級',
    description: '頂点と辺からなるグラフを配列で表現する方法を学びます。',
    points: ['隣接行列と隣接リストの考え方がある', '探索アルゴリズムの土台になる', '問題の規模で表現を選ぶ'],
    code: program({
      mainBody: `int graph[4][4] = {
    {0, 1, 1, 0},
    {1, 0, 0, 1},
    {1, 0, 0, 1},
    {0, 1, 1, 0}
};

printf("%d\\n", graph[0][2]);
printf("%d\\n", graph[1][2]);`
    })
  }),
  lesson({
    title: 'グラフィックス表現',
    category: 'グラフィックス',
    difficulty: '中級',
    description: '独自のグラフィック関数を使って図形を描く方法を学びます。',
    points: ['座標と色を組み合わせて描画する', 'ループで図形を繰り返し描ける', '`update_frame()` で反映する'],
    type: 'graphics',
    code: program({
      includes: ['#include "graphics.h"', '#include <math.h>'],
      mainBody: `clear_canvas();

for (int i = 0; i < 360; i += 20) {
    int x = 200 + (int)(120 * cos(i * 3.14159 / 180.0));
    int y = 200 + (int)(120 * sin(i * 3.14159 / 180.0));

    set_color((i * 3) % 255, (255 - i) % 255, (i + 80) % 255);
    draw_circle(x, y, 18);
}

set_color(255, 255, 255);
draw_rect(150, 150, 100, 100);
update_frame();`
    })
  }),
  lesson({
    title: '線形探索',
    category: 'アルゴリズム',
    difficulty: '中級',
    description: '先頭から順に調べる最も基本的な探索法を学びます。',
    points: ['未整列データにも使える', '見つかったら早めに終了できる', '探索成功と失敗の両方を扱う'],
    code: program({
      mainBody: `int values[6] = {4, 8, 15, 16, 23, 42};
int target = 16;
int index = -1;

for (int i = 0; i < 6; i++) {
    if (values[i] == target) {
        index = i;
        break;
    }
}

printf("%d\\n", index);`
    })
  }),
  lesson({
    title: '二分探索',
    category: 'アルゴリズム',
    difficulty: '中級',
    description: '整列済み配列を半分ずつ絞り込む探索法を学びます。',
    points: ['整列済みであることが前提', '探索範囲を毎回半分にできる', '`low` と `high` を更新する'],
    code: program({
      mainBody: `int values[7] = {3, 8, 12, 15, 19, 27, 31};
int target = 19;
int low = 0;
int high = 6;

while (low <= high) {
    int mid = (low + high) / 2;
    if (values[mid] == target) {
        printf("%d\\n", mid);
        break;
    } else if (values[mid] < target) {
        low = mid + 1;
    } else {
        high = mid - 1;
    }
}`
    })
  }),
  lesson({
    title: 'バブルソート',
    category: 'アルゴリズム',
    difficulty: '中級',
    description: '隣同士を比較して入れ替える基本ソートを学びます。',
    points: ['比較と交換を繰り返す', '外側ループごとに末尾が確定する', '動作を追いやすい入門アルゴリズム'],
    code: program({
      mainBody: `int values[5] = {5, 1, 4, 2, 3};

for (int i = 0; i < 4; i++) {
    for (int j = 0; j < 4 - i; j++) {
        if (values[j] > values[j + 1]) {
            int temp = values[j];
            values[j] = values[j + 1];
            values[j + 1] = temp;
        }
    }
}

for (int i = 0; i < 5; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  lesson({
    title: '選択ソート',
    category: 'アルゴリズム',
    difficulty: '中級',
    description: '未整列部分から最小値を選ぶソートを学びます。',
    points: ['1周ごとに1要素を確定する', '交換回数が比較的少ない', '最小値位置の探索と交換に分けて考える'],
    code: program({
      mainBody: `int values[5] = {9, 3, 7, 1, 5};

for (int i = 0; i < 4; i++) {
    int min_index = i;
    for (int j = i + 1; j < 5; j++) {
        if (values[j] < values[min_index]) {
            min_index = j;
        }
    }
    int temp = values[i];
    values[i] = values[min_index];
    values[min_index] = temp;
}

for (int i = 0; i < 5; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  lesson({
    title: '挿入ソート',
    category: 'アルゴリズム',
    difficulty: '中級',
    description: '整列済み部分へ要素を差し込むソートを学びます。',
    points: ['手札を並べる感覚に近い', 'ほぼ整列済みのデータで有効', 'シフト操作で挿入位置を空ける'],
    code: program({
      mainBody: `int values[5] = {8, 4, 6, 2, 9};

for (int i = 1; i < 5; i++) {
    int key = values[i];
    int j = i - 1;
    while (j >= 0 && values[j] > key) {
        values[j + 1] = values[j];
        j--;
    }
    values[j + 1] = key;
}

for (int i = 0; i < 5; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  lesson({
    title: 'マージソート',
    category: 'アルゴリズム',
    difficulty: '上級',
    description: '分割統治で配列を安定に整列させるマージソートを学びます。',
    points: ['配列を半分に分けて再帰的に整列する', 'マージ処理で2列を統合する', '安定ソートの代表例になる'],
    code: program({
      helpers: `void merge(int values[], int left, int mid, int right) {
    int temp[16];
    int i = left;
    int j = mid + 1;
    int k = 0;

    while (i <= mid && j <= right) {
        if (values[i] <= values[j]) {
            temp[k++] = values[i++];
        } else {
            temp[k++] = values[j++];
        }
    }

    while (i <= mid) {
        temp[k++] = values[i++];
    }
    while (j <= right) {
        temp[k++] = values[j++];
    }

    for (i = 0; i < k; i++) {
        values[left + i] = temp[i];
    }
}

void merge_sort(int values[], int left, int right) {
    if (left >= right) {
        return;
    }

    int mid = (left + right) / 2;
    merge_sort(values, left, mid);
    merge_sort(values, mid + 1, right);
    merge(values, left, mid, right);
}`,
      mainBody: `int values[6] = {9, 1, 7, 3, 6, 2};

merge_sort(values, 0, 5);
for (int i = 0; i < 6; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  lesson({
    title: 'クイックソート',
    category: 'アルゴリズム',
    difficulty: '上級',
    description: '基準値で分割しながら整列する高速ソートの基本を学びます。',
    points: ['pivot を決めて左右へ振り分ける', '平均的に高速なソート', '分割の実装を丁寧に追う'],
    code: program({
      helpers: `void quick_sort(int values[], int left, int right) {
    int i = left;
    int j = right;
    int pivot = values[(left + right) / 2];

    while (i <= j) {
        while (values[i] < pivot) {
            i++;
        }
        while (values[j] > pivot) {
            j--;
        }
        if (i <= j) {
            int temp = values[i];
            values[i] = values[j];
            values[j] = temp;
            i++;
            j--;
        }
    }

    if (left < j) {
        quick_sort(values, left, j);
    }
    if (i < right) {
        quick_sort(values, i, right);
    }
}`,
      mainBody: `int values[6] = {8, 2, 7, 1, 5, 4};

quick_sort(values, 0, 5);
for (int i = 0; i < 6; i++) {
    printf("%d\\n", values[i]);
}`
    })
  }),
  lesson({
    title: '幅優先探索',
    category: 'アルゴリズム',
    difficulty: '上級',
    description: 'グラフを近い頂点から順に調べる BFS の基本を学びます。',
    points: ['キューで探索順を管理する', '最短手数系の問題と相性がよい', '訪問済み管理が重要になる'],
    code: program({
      mainBody: `int graph[4][4] = {
    {0, 1, 1, 0},
    {1, 0, 0, 1},
    {1, 0, 0, 1},
    {0, 1, 1, 0}
};
int queue[4];
int visited[4] = {0};
int front = 0;
int rear = 0;

queue[rear++] = 0;
visited[0] = 1;

while (front < rear) {
    int node = queue[front++];
    printf("%d\\n", node);
    for (int next = 0; next < 4; next++) {
        if (graph[node][next] && !visited[next]) {
            visited[next] = 1;
            queue[rear++] = next;
        }
    }
}`
    })
  }),
  lesson({
    title: '深さ優先探索',
    category: 'アルゴリズム',
    difficulty: '上級',
    description: 'グラフを深くたどっていく DFS の基本を学びます。',
    points: ['再帰と相性がよい', '探索木のイメージを持つ', '訪問済み管理でループを防ぐ'],
    code: program({
      helpers: `void dfs(int graph[4][4], int visited[4], int node) {
    visited[node] = 1;
    printf("%d\\n", node);

    for (int next = 0; next < 4; next++) {
        if (graph[node][next] && !visited[next]) {
            dfs(graph, visited, next);
        }
    }
}`,
      mainBody: `int graph[4][4] = {
    {0, 1, 1, 0},
    {1, 0, 0, 1},
    {1, 0, 0, 1},
    {0, 1, 1, 0}
};
int visited[4] = {0};

dfs(graph, visited, 0);`
    })
  }),
  lesson({
    title: '動的計画法の基礎',
    category: 'アルゴリズム',
    difficulty: '上級',
    description: '部分問題の結果を再利用する動的計画法を学びます。',
    points: ['重複計算を避ける', '表を下から埋める考え方がある', '漸化式を見つけるのが重要になる'],
    code: program({
      mainBody: `int n = 10;
int fib[11] = {0};

fib[1] = 1;
for (int i = 2; i <= n; i++) {
    fib[i] = fib[i - 1] + fib[i - 2];
}

printf("%d\\n", fib[n]);`
    })
  }),
  lesson({
    title: '数値積分',
    category: '数値解析',
    difficulty: '上級',
    description: '台形公式を使って積分値を近似する方法を学びます。',
    points: ['連続関数を細かく分割して面積近似する', '分割数を増やすと精度が上がる傾向がある', '数値解析の基本例になる'],
    code: program({
      includes: ['#include <stdio.h>', '#include <math.h>'],
      helpers: `double f(double x) {
    return sin(x);
}`,
      mainBody: `double a = 0.0;
double b = 3.1415926535;
int n = 1000;
double h = (b - a) / n;
double sum = 0.0;

for (int i = 0; i < n; i++) {
    double x1 = a + i * h;
    double x2 = x1 + h;
    sum += (f(x1) + f(x2)) * h / 2.0;
}

printf("%.6f\\n", sum);`
    })
  }),
  lesson({
    title: 'ニュートン法',
    category: '数値解析',
    difficulty: '上級',
    description: '方程式の解を反復計算で求めるニュートン法を学びます。',
    points: ['初期値から少しずつ解へ近づく', '導関数の情報を使う', '収束しない場合もあることを意識する'],
    code: program({
      helpers: `double f(double x) {
    return x * x - 2.0;
}

double df(double x) {
    return 2.0 * x;
}`,
      mainBody: `double x = 1.0;

for (int i = 0; i < 6; i++) {
    x = x - f(x) / df(x);
}

printf("%.10f\\n", x);`
    })
  }),
  graphicsLesson({
    title: 'ドット描画入門',
    difficulty: '初級',
    description: 'set_pixel を使って点の集合から図形を作る基本を学びます。',
    points: ['1ピクセル単位で位置を制御する', 'for文でドット列を作れる', '小さな図形を組み合わせて絵を作る'],
    code: graphicsProgram({
      mainBody: `clear_canvas();
set_color(255, 255, 0);

for (int i = 0; i < 80; i++) {
    set_pixel(40 + i, 60);
    set_pixel(40, 60 + i);
    set_pixel(40 + i, 140);
    set_pixel(120, 60 + i);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: '座標軸と目盛り',
    difficulty: '初級',
    description: 'draw_line を使って座標軸と目盛りを描く方法を学びます。',
    points: ['横軸と縦軸を線で描ける', '一定間隔の目盛りをループで配置する', '座標系の感覚がつかめる'],
    code: graphicsProgram({
      mainBody: `clear_canvas();
set_color(220, 220, 220);
draw_line(30, 200, 370, 200);
draw_line(200, 30, 200, 370);

for (int i = 40; i <= 360; i += 40) {
    draw_line(i, 195, i, 205);
    draw_line(195, i, 205, i);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: 'カラーバー',
    difficulty: '初級',
    description: 'set_color と draw_rect で色の帯を描く基本を確認します。',
    points: ['描画色を切り替えて矩形を塗る', '座標と幅でレイアウトできる', 'RGB値の変化を視覚的に確認できる'],
    code: graphicsProgram({
      mainBody: `clear_canvas();

for (int i = 0; i < 8; i++) {
    set_color(i * 30, 100 + i * 15, 255 - i * 20);
    draw_rect(30 + i * 40, 80, 30, 240);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: '信号機を描く',
    difficulty: '初級',
    description: '円と矩形を組み合わせて信号機を描くレッスンです。',
    points: ['複数図形を重ねて絵を構成する', '背景となる部品を先に描く', '色の役割を考えて配置する'],
    code: graphicsProgram({
      mainBody: `clear_canvas();
set_color(80, 80, 80);
draw_rect(150, 40, 100, 260);
draw_rect(190, 300, 20, 70);

set_color(255, 0, 0);
draw_circle(200, 90, 30);
set_color(255, 220, 0);
draw_circle(200, 170, 30);
set_color(0, 200, 80);
draw_circle(200, 250, 30);

update_frame();`
    })
  }),
  graphicsLesson({
    title: '市松模様',
    difficulty: '初級',
    description: '矩形を格子状に並べて市松模様を描きます。',
    points: ['二重ループで行と列を処理する', '偶奇判定で色を切り替える', 'タイル状のレイアウトが作れる'],
    code: graphicsProgram({
      mainBody: `clear_canvas();

for (int row = 0; row < 8; row++) {
    for (int col = 0; col < 8; col++) {
        if ((row + col) % 2 == 0) {
            set_color(240, 240, 240);
        } else {
            set_color(70, 70, 70);
        }
        draw_rect(40 + col * 40, 40 + row * 40, 40, 40);
    }
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: '同心円のターゲット',
    difficulty: '初級',
    description: '中心をそろえた複数の円を描き、的のような図形を作ります。',
    points: ['同じ中心座標で半径だけ変える', '色を交互に切り替える', 'draw_circle の使い方に慣れる'],
    code: graphicsProgram({
      mainBody: `clear_canvas();

set_color(255, 255, 255);
draw_circle(200, 200, 120);
set_color(255, 0, 0);
draw_circle(200, 200, 90);
set_color(255, 255, 255);
draw_circle(200, 200, 60);
set_color(255, 0, 0);
draw_circle(200, 200, 30);

update_frame();`
    })
  }),
  graphicsLesson({
    title: '放射線アート',
    difficulty: '中級',
    description: '中心から外へ向かう線を多数描いて放射模様を作ります。',
    points: ['角度ごとに座標を計算する', 'math.h の sin と cos を使う', '反復描画で複雑な模様を作れる'],
    code: graphicsProgram({
      includes: ['#include "graphics.h"', '#include <math.h>'],
      mainBody: `clear_canvas();

for (int degree = 0; degree < 360; degree += 10) {
    double rad = degree * 3.1415926535 / 180.0;
    int x = 200 + (int)(160 * cos(rad));
    int y = 200 + (int)(160 * sin(rad));

    set_color((degree * 2) % 255, 180, 255 - degree / 2);
    draw_line(200, 200, x, y);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: '棒グラフの描画',
    difficulty: '中級',
    description: '配列の値を高さとして棒グラフを描く方法を学びます。',
    points: ['データ値を図形サイズへ変換する', '繰り返し処理で複数の棒を並べる', '視覚化の基本パターンになる'],
    code: graphicsProgram({
      mainBody: `int values[6] = {120, 180, 90, 220, 160, 130};

clear_canvas();
for (int i = 0; i < 6; i++) {
    set_color(70 + i * 25, 120, 240 - i * 20);
    draw_rect(40 + i * 55, 320 - values[i], 35, values[i]);
}

set_color(255, 255, 255);
draw_line(30, 320, 380, 320);
update_frame();`
    })
  }),
  graphicsLesson({
    title: '折れ線グラフの描画',
    difficulty: '中級',
    description: 'データ点を線でつないで折れ線グラフを描く方法を学びます。',
    points: ['点列の座標を配列から作る', '隣り合う点を draw_line で結ぶ', 'グラフ表現の基本を確認できる'],
    code: graphicsProgram({
      mainBody: `int values[7] = {80, 140, 120, 220, 200, 260, 180};
int x[7];
int y[7];

clear_canvas();
set_color(255, 255, 255);
draw_line(40, 320, 360, 320);
draw_line(40, 40, 40, 320);

for (int i = 0; i < 7; i++) {
    x[i] = 60 + i * 45;
    y[i] = 320 - values[i];
}

set_color(0, 220, 255);
for (int i = 0; i < 6; i++) {
    draw_line(x[i], y[i], x[i + 1], y[i + 1]);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: 'スマイリーフェイス',
    difficulty: '中級',
    description: '円と点、線を組み合わせて顔のイラストを描きます。',
    points: ['部品を分けて考えると絵を組み立てやすい', '大きな図形から順に描く', '表情を少ない図形で表現できる'],
    code: graphicsProgram({
      mainBody: `clear_canvas();

set_color(255, 220, 0);
draw_circle(200, 200, 120);
set_color(0, 0, 0);
draw_circle(160, 170, 15);
draw_circle(240, 170, 15);
draw_line(140, 250, 260, 250);
draw_line(140, 250, 160, 270);
draw_line(260, 250, 240, 270);

update_frame();`
    })
  }),
  graphicsLesson({
    title: '家のイラスト',
    difficulty: '中級',
    description: '矩形と線を組み合わせて家のイラストを描く練習です。',
    points: ['建物を複数パーツに分けて描く', '屋根は線で形を作る', '位置関係を考えて配置する'],
    code: graphicsProgram({
      mainBody: `clear_canvas();

set_color(180, 120, 70);
draw_rect(110, 160, 180, 140);
set_color(120, 70, 40);
draw_rect(180, 220, 40, 80);
set_color(180, 230, 255);
draw_rect(135, 190, 35, 35);
draw_rect(230, 190, 35, 35);

set_color(255, 80, 80);
draw_line(100, 160, 200, 90);
draw_line(200, 90, 300, 160);
draw_line(100, 160, 300, 160);

update_frame();`
    })
  }),
  graphicsLesson({
    title: '時計盤の描画',
    difficulty: '中級',
    description: '円周上へ目盛りを置いて時計盤のような図形を描きます。',
    points: ['角度ごとに点の位置を計算する', '外側と内側の座標を線で結ぶ', '円運動の座標計算に慣れる'],
    code: graphicsProgram({
      includes: ['#include "graphics.h"', '#include <math.h>'],
      mainBody: `clear_canvas();
set_color(255, 255, 255);
draw_circle(200, 200, 140);

for (int i = 0; i < 12; i++) {
    double rad = (i * 30 - 90) * 3.1415926535 / 180.0;
    int x1 = 200 + (int)(110 * cos(rad));
    int y1 = 200 + (int)(110 * sin(rad));
    int x2 = 200 + (int)(130 * cos(rad));
    int y2 = 200 + (int)(130 * sin(rad));
    draw_line(x1, y1, x2, y2);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: 'アニメーションの基本',
    difficulty: '中級',
    description: 'wait_msec と update_frame を使って動く矩形を作ります。',
    points: ['フレームごとに画面を描き直す', '少しずつ座標を変えると動きになる', '待機時間で速さを調整できる'],
    code: graphicsProgram({
      mainBody: `for (int frame = 0; frame < 40; frame++) {
    clear_canvas();
    set_color(0, 200, 255);
    draw_rect(20 + frame * 8, 170, 60, 60);
    update_frame();
    wait_msec(50);
}`
    })
  }),
  graphicsLesson({
    title: 'バウンドボール',
    difficulty: '中級',
    description: '位置と速度を更新して跳ね返るボールを描きます。',
    points: ['位置と速度を変数で管理する', '壁に当たったら進行方向を反転する', '簡単な物理っぽい動きを作れる'],
    code: graphicsProgram({
      mainBody: `int x = 80;
int y = 90;
int vx = 5;
int vy = 4;

for (int frame = 0; frame < 80; frame++) {
    clear_canvas();
    set_color(255, 180, 0);
    draw_circle(x, y, 20);
    update_frame();
    wait_msec(30);

    x += vx;
    y += vy;

    if (x <= 20 || x >= 380) {
        vx = -vx;
    }
    if (y <= 20 || y >= 380) {
        vy = -vy;
    }
}`
    })
  }),
  graphicsLesson({
    title: 'キーボードで点を動かす',
    difficulty: '中級',
    description: 'get_key を使ってドットの位置を操作する基本を学びます。',
    points: ['入力文字で座標を更新する', '毎フレーム描き直して見た目を更新する', '対話的なプログラムの土台になる'],
    code: graphicsProgram({
      mainBody: `int x = 200;
int y = 200;

for (int step = 0; step < 20; step++) {
    int key = get_key();
    if (key == 'w') y -= 10;
    if (key == 's') y += 10;
    if (key == 'a') x -= 10;
    if (key == 'd') x += 10;

    clear_canvas();
    set_color(255, 255, 255);
    draw_circle(x, y, 8);
    update_frame();
}`
    })
  }),
  graphicsLesson({
    title: 'キーボードで矩形を移動する',
    difficulty: '中級',
    description: 'get_key で矩形を移動させる簡易プレイヤー制御を学びます。',
    points: ['移動量を一定にすると操作しやすい', '画面外へ出ないように制限できる', 'ゲーム的な処理の基本になる'],
    code: graphicsProgram({
      mainBody: `int x = 180;
int y = 180;

for (int step = 0; step < 24; step++) {
    int key = get_key();
    if (key == 'w' && y > 20) y -= 15;
    if (key == 's' && y < 340) y += 15;
    if (key == 'a' && x > 20) x -= 15;
    if (key == 'd' && x < 340) x += 15;

    clear_canvas();
    set_color(0, 220, 120);
    draw_rect(x, y, 40, 40);
    update_frame();
}`
    })
  }),
  graphicsLesson({
    title: '簡易お絵かきカーソル',
    difficulty: '上級',
    description: '移動した軌跡を残して簡易ペイントのように描く方法を学びます。',
    points: ['過去の座標を消さずに残す', '入力に応じてカーソル位置を変える', 'set_pixel で描画点を追加できる'],
    code: graphicsProgram({
      mainBody: `int x = 200;
int y = 200;

clear_canvas();
set_color(255, 255, 255);

for (int step = 0; step < 40; step++) {
    int key = get_key();
    if (key == 'w') y -= 6;
    if (key == 's') y += 6;
    if (key == 'a') x -= 6;
    if (key == 'd') x += 6;

    for (int dx = -2; dx <= 2; dx++) {
        for (int dy = -2; dy <= 2; dy++) {
            set_pixel(x + dx, y + dy);
        }
    }
    update_frame();
}`
    })
  }),
  graphicsLesson({
    title: 'ランダムスター生成',
    difficulty: '中級',
    description: '乱数で星空のような点群を描くレッスンです。',
    points: ['rand で位置や色を決める', 'ランダムデータを視覚化できる', 'set_pixel の大量描画に慣れる'],
    code: graphicsProgram({
      includes: ['#include "graphics.h"', '#include <stdlib.h>', '#include <time.h>'],
      mainBody: `srand((unsigned)time(NULL));
clear_canvas();

for (int i = 0; i < 300; i++) {
    int x = rand() % 400;
    int y = rand() % 400;
    int c = 180 + rand() % 76;
    set_color(c, c, 255);
    set_pixel(x, y);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: 'サイン波の描画',
    difficulty: '上級',
    description: 'sin を使って波形をキャンバスへプロットする方法を学びます。',
    points: ['数学関数を座標へ変換する', '横軸に沿って点を連続配置する', '関数の形を視覚化できる'],
    code: graphicsProgram({
      includes: ['#include "graphics.h"', '#include <math.h>'],
      mainBody: `clear_canvas();
set_color(255, 255, 255);
draw_line(20, 200, 380, 200);

set_color(0, 220, 255);
for (int x = 20; x <= 380; x++) {
    double angle = (x - 20) / 360.0 * 4.0 * 3.1415926535;
    int y = 200 + (int)(80 * sin(angle));
    set_pixel(x, y);
}

update_frame();`
    })
  }),
  graphicsLesson({
    title: 'リサージュ曲線アニメーション',
    difficulty: '上級',
    description: '異なる周期の sin と cos を組み合わせて曲線を描きます。',
    points: ['複数の周期関数で複雑な軌跡を作れる', 'フレームごとに少しずつ描いていく', '数学とアニメーションを結びつけられる'],
    code: graphicsProgram({
      includes: ['#include "graphics.h"', '#include <math.h>'],
      mainBody: `clear_canvas();
set_color(0, 255, 180);

for (int t = 0; t <= 720; t += 2) {
    double rad = t * 3.1415926535 / 180.0;
    int x = 200 + (int)(130 * sin(3 * rad));
    int y = 200 + (int)(130 * cos(2 * rad));
    set_pixel(x, y);

    if (t % 24 == 0) {
        update_frame();
        wait_msec(20);
    }
}

update_frame();`
    })
  })
];

const lessonSupplements = {
  '整数型の使い分け': {
    description:
      'C の整数型サイズは規格で固定されておらず、処理系ごとに違う点も押さえて読み進めます。',
    points: [
      '`short`、`int`、`long` のサイズを 2 / 4 / 8 バイトのように決め打ちせず、実際の環境では `sizeof` で確認します。',
      'サイズだけでなく取り得る範囲も処理系依存なので、厳密な範囲確認では `<limits.h>` や `<stdint.h>` も併用します。'
    ]
  },
  '文字型とASCII': {
    description:
      '`char` の符号やビット幅は実装依存であることも含めて、文字と数値の関係を整理します。',
    points: [
      '`char` は多くの環境で 8 ビットですが、規格上はビット幅も符号付きかどうかも処理系に依存します。',
      '数値としての振る舞いを明確にしたいときは、`char` ではなく `signed char` や `unsigned char` を明示して使います。'
    ]
  },
  '定数とconst': {
    description:
      '`const` を付けたオブジェクトは初期化後に変更しないという前提で扱うことを明確にします。',
    points: [
      '`const` を付けた変数は初期化後に再代入できず、「あとで値を入れ替える変数」としては使いません。',
      'ポインタ経由で無理に書き換えるのも未定義動作になり得るため、`const` の約束を破らない設計を意識します。'
    ]
  },
  'インクリメントとデクリメント': {
    description:
      '前置と後置で式の評価順が変わる点まで含めて、`++` と `--` の意味を整理します。',
    points: [
      '`++i` は増やしてからその値を式で使い、`i++` は現在値を使ってから増やします。',
      '単独の文では差が見えにくくても、複合式の中では結果が変わるので、読みやすさを優先して使い分けます。'
    ]
  },
  '論理演算子': {
    description:
      '論理演算子とビット演算子を混同しないように、`&&` と `&` の違いも補足します。',
    points: [
      '`&&` と `||` は真偽値として判定する論理演算子で、左辺だけで結果が決まると右辺を評価しない短絡評価を行います。',
      '`&` や `|` は整数の各ビットを計算する別の演算子なので、条件式では `&&` / `||` と混同しないようにします。'
    ]
  },
  'scanfで整数入力': {
    description:
      '値を読むだけでなく、`scanf` の戻り値で入力成功を確かめる視点も追加します。',
    points: [
      '`scanf` の戻り値は正しく読み取れた項目数なので、期待どおりに 1 項目読めたかを確認できます。',
      '1 行の文字列入力では `gets` は使わず、最大長を指定できる `fgets` を使う方が安全です。'
    ]
  },
  'scanfで複数入力': {
    description:
      '複数項目入力では、何個読めたかを戻り値で確認する習慣も付けます。',
    points: [
      '`scanf` の戻り値を見れば、2 個読むつもりで本当に 2 個とも読めたかを判定できます。',
      '複数入力でも、行全体を安全に受けてから解析したい場面では `fgets` と変換関数の組み合わせが有効です。'
    ]
  },
  'ポインタと配列': {
    description:
      '似た書き方ができても、配列そのものとポインタ変数は別物である点まで説明します。',
    points: [
      '配列名が先頭要素へのポインタのように扱われる場面はありますが、配列自体がポインタ変数になるわけではありません。',
      '`sizeof(array)` は配列全体の大きさを返し、`sizeof(ptr)` はアドレスを入れる変数自身の大きさを返します。'
    ]
  },
  'sizeofとメモリレイアウト': {
    description:
      '`sizeof` が関数ではなく演算子であり、配列とポインタで結果が変わる点も丁寧に押さえます。',
    points: [
      '`sizeof` は関数呼び出しではなく演算子なので、型にも式にも適用できます。',
      '配列へ適用したときは配列全体のバイト数を返し、ポインタへ適用したときはポインタ自身のサイズだけを返します。'
    ]
  }
};

const appendLessonSupplement = (lessonItem) => {
  const supplement = lessonSupplements[lessonItem.title];

  if (!supplement) {
    return lessonItem;
  }

  const extraSection = lines(
    '',
    '補足:',
    ...supplement.points.map((point, index) => `${index + 1}. ${point}`)
  );

  return {
    ...lessonItem,
    description: `${lessonItem.description} ${supplement.description}`.trim(),
    content: `${lessonItem.content}${extraSection}`
  };
};

export const lessons = lessonSeed.map((lessonItem, index) => ({
  id: index + 1,
  ...appendLessonSupplement(lessonItem)
}));
